/** Domain models & send state machine — SPEC §12 / §16.2 */

import type { DeliveryState, ProfileMedia } from "@veto/protocol";

export type AssuranceMode = "pwa" | "native" | "high-risk";

export interface Account {
  rootId: string;
  protocolVersion: number;
  assuranceMode: AssuranceMode;
  displayName: string;
  /** Short public bio ("About"), synced encrypted to contacts. */
  about?: string;
  /** Local avatar (photo / GIF / short video loop) — sealed at rest. */
  avatar?: ProfileMedia | null;
  createdAt: number;
  messagingPublicKeyHex: string;
  hybridPublicKeyHex: string;
}

export interface Contact {
  id: string;
  displayName: string;
  /** Bio announced by the contact via encrypted profile updates. */
  about?: string;
  /** Avatar received via encrypted profile updates — sealed at rest. */
  avatar?: ProfileMedia | null;
  /** Monotonic guard against stale/replayed profile updates. */
  profileUpdatedAt?: number;
  messagingPublicKeyHex: string;
  hybridPublicKeyHex: string;
  /** Peer BIP-340 x-only transport (secp256k1) key — from the invite bundle (NIP-01 binding). */
  transportPublicKeyHex?: string;
  safetyNumber: string;
  verificationState: "unverified" | "verified" | "changed";
  blockedAt?: number;
  /**
   * Message request (FR-CON-001/004): contacts that reached us first — they
   * hold our invite but we never added them. `pending` until the user accepts;
   * undefined = accepted (added by us, or legacy rows).
   */
  requestState?: "pending";
  /** How the contact was learned — invite we opened vs. their first message. */
  addedVia?: "invite" | "message" | "keys";
  /** Epoch ms the contact record was created. */
  addedAt?: number;
}

/** FR-CON-003 — append-only local security events */
export interface SecurityEvent {
  id: string;
  kind: "key_change" | "device_change" | "unverified_contact" | "message_request";
  contactId?: string;
  detail: string;
  createdAt: number;
  acknowledged: boolean;
}

export interface Conversation {
  id: string;
  type: "direct" | "group";
  title: string;
  contactId?: string;
  lastMessagePreview: string;
  lastMessageAt: number;
  unread: number;
  pinned: boolean;
  muted: boolean;
  /** Hidden from main list until unarchived */
  archived?: boolean;
  /**
   * Message request (first contact from someone we never added): shown with
   * Accept / Block and no read signals, auto-downloads or direct P2P until accepted.
   */
  requestPending?: boolean;
}

export interface Reaction {
  emoji: string;
  senderDevice: string;
  at: number;
}

/**
 * Delivery channel actually used for an outbound message — layered fallbacks in
 * priority order: peer mesh → private Nostr relays → community (public mesh) →
 * Supabase realtime (last resort). UI shows this per message (NET-REL-006).
 */
export type TransportChannel =
  | "direct"
  | "direct+relay"
  | "relay"
  | "expanded"
  | "community"
  | "realtime";

/** Result of a publish attempt across all transport layers. */
export interface PublishOutcome {
  status: "queued" | "sent" | "backed_up";
  transport?: TransportChannel;
}

export interface MessageEvent {
  eventId: string;
  conversationId: string;
  senderDevice: string;
  direction: "out" | "in";
  body: string;
  logicalCounter: number;
  createdAt: number;
  status: DeliveryState;
  /** Transport channel used for delivery (UI transparency — NET-REL-006). */
  transport?: TransportChannel;
  sealedJson?: string;
  contentType: number;
  replyToEventId?: string;
  reactions?: Reaction[];
  editedAt?: number;
  deletedForEveryone?: boolean;
  deletedForMe?: boolean;
  /** Local-only star (H4). */
  starred?: boolean;
  /** Local pin within conversation (H4). */
  pinned?: boolean;
  /** Disappearing timer expiry (epoch ms); local delete when past. */
  expiresAt?: number;
  /** Timer that applied when sent (seconds). */
  disappearTimerSec?: number;
  /** View-once media — burned after first open on this device. */
  viewOnce?: boolean;
  viewOnceOpenedAt?: number;
  /** Group messages: the local contact id of the (authenticated) sender. */
  senderContactId?: string;
}

export const EDIT_WINDOW_MS = 15 * 60 * 1000;
export const DELETE_EVERYONE_WINDOW_MS = 60 * 60 * 1000;

export function canEditMessage(msg: MessageEvent, now = Date.now()): boolean {
  if (msg.direction !== "out" || msg.deletedForEveryone || msg.deletedForMe) return false;
  return now - msg.createdAt <= EDIT_WINDOW_MS;
}

export function canDeleteForEveryone(msg: MessageEvent, now = Date.now()): boolean {
  if (msg.direction !== "out" || msg.deletedForEveryone) return false;
  return now - msg.createdAt <= DELETE_EVERYONE_WINDOW_MS;
}

export function applyReaction(
  msg: MessageEvent,
  emoji: string,
  senderDevice: string,
): MessageEvent {
  const reactions = [...(msg.reactions ?? [])];
  const idx = reactions.findIndex((r) => r.senderDevice === senderDevice && r.emoji === emoji);
  if (idx >= 0) reactions.splice(idx, 1);
  else {
    const prev = reactions.findIndex((r) => r.senderDevice === senderDevice);
    if (prev >= 0) reactions.splice(prev, 1);
    reactions.push({ emoji, senderDevice, at: Date.now() });
  }
  return { ...msg, reactions };
}

export function toggleStarred(msg: MessageEvent): MessageEvent {
  return { ...msg, starred: !msg.starred };
}

export function togglePinned(msg: MessageEvent): MessageEvent {
  return { ...msg, pinned: !msg.pinned };
}

export function tombstoneEveryone(msg: MessageEvent): MessageEvent {
  return {
    ...msg,
    deletedForEveryone: true,
    body: "",
    reactions: [],
  };
}

export interface DraftRecord {
  conversationId: string;
  text: string;
  replyToEventId?: string;
  updatedAt: number;
}

export { nextRetryAt, shouldRetry, RETRY_DELAYS_MS } from "./outboxRetry";
export {
  createGroupRecord,
  removeMember,
  addMembers,
  setMemberRole,
  renameGroup,
  canDecryptGroupEpoch,
  assertSelfIsAdmin,
  evaluateInboundGroupKey,
  type GroupRecord,
  type GroupMember,
  type GroupKeyDecision,
} from "./groups";
export {
  VetoError,
  isVetoError,
  userFacingMessage,
  type VetoErrorCode,
} from "./errors";
export {
  DEVICE_LINK_TTL_MS,
  createLocalDevice,
  createLinkOffer,
  encodeLinkOffer,
  decodeLinkOffer,
  approveLinkOffer,
  linkApprovalBytes,
  verifyLinkApproval,
  revokeDevice,
} from "./devices";
export {
  parseFormatted,
  plainPreview,
  listPreview,
  mapFormatted,
  containsDangerousHtml,
  type FormatSpan,
} from "./formatting";
export { buildForwardedMessage, isForwardedBody } from "./forward";
export {
  togglePinnedConversation,
  toggleMutedConversation,
  markConversationRead,
  markConversationUnread,
  blockContact,
  unblockContact,
  sortConversations,
} from "./conversationActions";
export {
  DISAPPEAR_OPTIONS,
  expireAt,
  isExpired,
  filterExpired,
  type DisappearTimerSec,
} from "./disappearing";
export { extractMentions, mentionToken, mentionsAnyName, mentionsIdentity, mentionsAnyone } from "./mentions";
export {
  createPoll,
  castVote,
  closePoll,
  encodePollWire,
  decodePollWire,
  mergePollVoter,
  type PollRecord,
  type PollOption,
} from "./polls";
export {
  createChatLock,
  verifyChatPin,
  hashChatPin,
  type ChatLockRecord,
} from "./chatLock";
export { extractUrls, linkPreviewLabel, type LocalLinkChip } from "./linkPreview";
export {
  encodeSystemEvent,
  decodeSystemEvent,
  isSystemEventBody,
  type SystemEventKind,
  type SystemEventPayload,
} from "./systemEvents";
export {
  encodeInvite,
  encodeCompactInvite,
  compactInviteToBundleJson,
  decodeInvite,
  normalizeSafetyDigits,
  INVITE_LINK_VERSION,
  COMPACT_BUNDLE_BYTES,
  encodeInviteToken,
  encodeInviteLink,
  decodeInviteToken,
  extractInvite,
  sanitizeDisplayName,
  type ExtractedInvite,
  type InviteLinkPayload,
  type InvitePayloadV1,
  type InvitePayloadV2,
  type InvitePayloadV3,
  type InvitePayload,
} from "./invite";
export {
  VERIFY_QR_PREFIX,
  encodeVerifyQr,
  decodeVerifyQr,
} from "./verifyQr";

export function archiveConversation(c: Conversation): Conversation {
  return { ...c, archived: true, pinned: false };
}

export function unarchiveConversation(c: Conversation): Conversation {
  return { ...c, archived: false };
}

export type SendPhase =
  | "DRAFT"
  | "ENCRYPTED_LOCAL"
  | "ROUTING"
  | "SENT"
  | "BACKED_UP"
  | "DELIVERED"
  | "READ"
  | "FAILED";

const SEND_FLOW: Record<SendPhase, DeliveryState> = {
  DRAFT: "draft",
  ENCRYPTED_LOCAL: "queued",
  ROUTING: "queued",
  SENT: "sent",
  BACKED_UP: "backed_up",
  DELIVERED: "delivered",
  READ: "read",
  FAILED: "failed",
};

export function phaseToDelivery(phase: SendPhase): DeliveryState {
  return SEND_FLOW[phase];
}

/** FR-MSG-001: message must be ENCRYPTED_LOCAL before UI reports queued/sent. */
export function advanceSendPhase(current: SendPhase, event: "encrypt" | "route_ack" | "backup_ack" | "deliver_ack" | "read_ack" | "fail"): SendPhase {
  switch (event) {
    case "encrypt":
      return current === "DRAFT" ? "ENCRYPTED_LOCAL" : current;
    case "route_ack":
      if (current === "ENCRYPTED_LOCAL" || current === "ROUTING") return "SENT";
      return current;
    case "backup_ack":
      if (current === "SENT" || current === "ROUTING" || current === "ENCRYPTED_LOCAL") return "BACKED_UP";
      return current;
    case "deliver_ack":
      return "DELIVERED";
    case "read_ack":
      return "READ";
    case "fail":
      return "FAILED";
    default:
      return current;
  }
}

let eventIdPrefix = "";

/**
 * Review L2: locally minted ids start with the first 16 hex of OUR identity key.
 * Receivers only accept a payload whose id carries the authenticated sender's
 * prefix, so no peer can mint (and pre-empt) another sender's message id.
 */
export function setEventIdPrefix(messagingPublicKeyHex: string | null): void {
  const p = (messagingPublicKeyHex ?? "").toLowerCase().slice(0, 16);
  eventIdPrefix = /^[0-9a-f]{16}$/.test(p) ? p : "";
}

/** Was `id` minted by the holder of `messagingPublicKeyHex`? */
export function eventIdOwnedBy(id: string, messagingPublicKeyHex: string): boolean {
  return id.startsWith(messagingPublicKeyHex.toLowerCase().slice(0, 16));
}

/** Fresh 64-hex id, owned by `ownerMessagingKeyHex` (default: the unlocked account). */
export function createEventId(ownerMessagingKeyHex?: string): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  const owner = ownerMessagingKeyHex ? ownerMessagingKeyHex.toLowerCase().slice(0, 16) : eventIdPrefix;
  return /^[0-9a-f]{16}$/.test(owner) ? owner + hex.slice(16) : hex;
}

export function dedupeMessages(events: MessageEvent[]): MessageEvent[] {
  const seen = new Set<string>();
  const out: MessageEvent[] = [];
  for (const e of events) {
    if (seen.has(e.eventId)) continue;
    seen.add(e.eventId);
    out.push(e);
  }
  return out;
}
